import { defineStore } from 'pinia'

import { useUsersStore } from 'src/stores/users.js'

import { dismissAnnouncement, getAnnouncements } from 'src/api/user.js'
import { pollingActions } from 'src/services/poller/poller.js'

const FETCH_ANNOUNCEMENT_INTERVAL_MS = 1000 * 60 * 5

export const useAnnouncementsStore = defineStore('announcements', {
  state: () => ({
    announcements: [],
    supportsAnnouncements: true,
    poller: null,
    adminActions: {},
  }),
  getters: {
    unreadAnnouncementsCount() {
      if (!useUsersStore().currentUser) {
        return 0
      }

      const unread = this.announcements.filter(
        (announcement) => !(announcement.inactive || announcement.read),
      )
      return unread.length
    },
  },
  actions: {
    async fetchAnnouncements() {
      if (!this.supportsAnnouncements) return

      const currentUser = useUsersStore().currentUser
      const isAdmin =
        currentUser &&
        currentUser.privileges.has('announcements_manage_announcements')

      try {
        if (isAdmin) {
          this.adminActions = await import('src/api/admin.js')
        } else {
          const { data } = await getAnnouncements({})
          this.announcements = data
          return
        }

        const { data: all } = await this.adminActions.getAnnouncements({})

        const { data: visible } = await getAnnouncements({})

        const visibleObject = visible.reduce((a, c) => {
          a[c.id] = c
          return a
        }, {})

        const getWithinVisible = (announcement) =>
          visibleObject[announcement.id]

        all.forEach((announcement) => {
          const visibleAnnouncement = getWithinVisible(announcement)
          if (!visibleAnnouncement) {
            announcement.inactive = true
          } else {
            announcement.read = visibleAnnouncement.read
          }
        })

        this.announcements = all
      } catch (error) {
        // If and only if backend does not support announcements, it would return 404.
        // In this case, silently ignores it.
        if (error?.statusCode === 404) {
          this.supportsAnnouncements = false
        } else {
          throw error
        }
      }
    },
    markAnnouncementAsRead(id) {
      return dismissAnnouncement({
        id,
      }).then(() => {
        const index = this.announcements.findIndex((a) => a.id === id)

        if (index < 0) {
          return
        }

        this.announcements[index].read = true
      })
    },
    ...pollingActions(
      (store) => store.fetchAnnouncements(),
      FETCH_ANNOUNCEMENT_INTERVAL_MS,
    ),
    postAnnouncement({ content, startsAt, endsAt, allDay }) {
      return this.adminActions
        .postAnnouncement({
          content,
          startsAt,
          endsAt,
          allDay,
        })
        .then(() => {
          return this.fetchAnnouncements()
        })
    },
    editAnnouncement({ id, content, startsAt, endsAt, allDay }) {
      return this.adminActions
        .editAnnouncement({
          id,
          content,
          startsAt,
          endsAt,
          allDay,
        })
        .then(() => {
          return this.fetchAnnouncements()
        })
    },
    deleteAnnouncement(id) {
      return this.adminActions
        .deleteAnnouncement({
          id,
        })
        .then(() => {
          return this.fetchAnnouncements()
        })
    },
  },
})
